(function () {
  'use strict';

  const PROTOCOL_VERSION = 1;
  const HOST_INIT = 'HOST_INIT';
  const CHILD_READY = 'CHILD_READY';
  const HOST_BOOTSTRAP = 'HOST_BOOTSTRAP';
  const APP_READY = 'APP_READY';
  const CHILD_ERROR = 'CHILD_ERROR';
  const AUTH_EXPIRED = 'AUTH_EXPIRED';
  const ALLOWED_PARAMS = new Set([
    'assetId',
    'assetTemplateId',
    'agentId',
    'apiKeyId',
    'category',
    'customerId',
    'dashboardId',
    'deviceId',
    'embedded',
    'projectId',
    'tab',
    'varId',
    'virtualGroupId',
  ]);

  angular.module('commonServices').config([
    '$urlServiceProvider',
    function ($urlServiceProvider) {
      if (isEmbedded()) {
        $urlServiceProvider.deferIntercept();
      }
    },
  ]);

  angular
    .module('commonServices')
    .service('EmbeddedHostBridge', EmbeddedHostBridge);

  EmbeddedHostBridge.$inject = [
    '$window',
    '$state',
    '$urlService',
    '$translate',
    '$rootScope',
    'LoopBackAuth',
  ];

  function EmbeddedHostBridge(
    $window,
    $state,
    $urlService,
    $translate,
    $rootScope,
    LoopBackAuth
  ) {
    let expectedRole;
    let parentOrigin;
    let instanceId;
    let initialized = false;
    let listening = false;
    let readySent = false;
    let bootstrapStarted = false;

    this.initialize = function initialize(role) {
      if (!isEmbedded() || initialized) {
        return false;
      }

      expectedRole = role;
      parentOrigin = getReferrerOrigin($window.document.referrer);
      if (!parentOrigin || $window.parent === $window) {
        console.error('Embedded mode requires an identifiable parent origin.');
        return false;
      }

      initialized = true;
      $window.addEventListener('message', onMessage);
      return true;
    };

    this.isEmbedded = function () { return initialized; };
    this.openApplication = function (appKey, params) {
      if (initialized && (appKey === 'kpi-engine' || appKey === 'api-keys')) send('OPEN_APPLICATION', { appKey: appKey, params: params });
    };

    this.notifyAuthExpired = function notifyAuthExpired() {
      if (initialized) {
        send(AUTH_EXPIRED, {});
      }
    };

    function onMessage(event) {
      if (
        event.source !== $window.parent ||
        event.origin !== parentOrigin ||
        !isBridgeMessage(event.data)
      ) {
        return;
      }

      const message = event.data;
      if (message.type === HOST_INIT) {
        if (instanceId !== message.instanceId) {
          instanceId = message.instanceId;
          readySent = false;
          bootstrapStarted = false;
        }
        if (readySent && bootstrapStarted) {
          return;
        }
        readySent = true;
        send(CHILD_READY, {});
        return;
      }

      if (
        message.type !== HOST_BOOTSTRAP ||
        !instanceId ||
        message.instanceId !== instanceId ||
        bootstrapStarted
      ) {
        return;
      }

      if (!isBootstrapPayload(message.payload, expectedRole)) {
        send(CHILD_ERROR, 'Invalid host bootstrap payload.');
        return;
      }

      bootstrapStarted = true;
      bootstrap(message.payload);
    }

    function bootstrap(payload) {
      const auth = payload.auth;
      const navigation = payload.navigation;
      const state = $state.get(navigation.legacyState);
      if (!state || !isAllowedState(navigation.legacyState, expectedRole)) {
        send(CHILD_ERROR, 'Legacy destination is not allowed.');
        return;
      }

      LoopBackAuth.setUser(auth.token, auth.userId);
      LoopBackAuth.save();
      applyTheme(payload.theme);
      $window.localStorage.setItem('lang', payload.language);

      const params = sanitizeStateParams(navigation.stateParams);
      if (expectedRole !== 'Manager') {
        params.customerId = navigation.customerId;
      }
      if (navigation.projectId) {
        params.projectId = navigation.projectId;
      }
      if (
        (expectedRole === 'SupportAdmin' || expectedRole === 'SuperAdmin') &&
        auth.varId
      ) {
        params.varId = auth.varId;
      }
      params.embedded = 'true';

      if (!listening) {
        $urlService.listen();
        listening = true;
      }

      Promise.resolve($translate.use(payload.language))
        .then(function () {
          return $state.go(navigation.legacyState, params, {
            location: false,
            reload: true,
          });
        })
        .then(function () {
          $rootScope.$evalAsync(function () {
            send(APP_READY, { state: navigation.legacyState });
          });
        })
        .catch(function (error) {
          console.error('Embedded legacy bootstrap failed.', error);
          send(CHILD_ERROR, 'Legacy application failed to start.');
        });
    }

    function applyTheme(theme) {
      const isDarkMode = theme === 'dark';
      $rootScope.darkMode = isDarkMode;
      $window.localStorage.setItem('dark-mode-toggle', theme);

      const themeLinks = Array.from(
        $window.document.querySelectorAll('link[rel="stylesheet"]')
      ).filter(function (link) {
        const pathname = new URL(link.href, $window.location.href).pathname;
        return /\/main(?:-dark)?\.css$/.test(pathname);
      });

      if (themeLinks.length === 1) {
        const link = themeLinks[0];
        const url = new URL(link.href, $window.location.href);
        url.pathname = url.pathname.replace(
          /\/main(?:-dark)?\.css$/,
          isDarkMode ? '/main-dark.css' : '/main.css'
        );
        link.href = url.toString();
        link.media = 'all';
        link.disabled = false;
      } else {
        themeLinks.forEach(function (link) {
          const pathname = new URL(link.href, $window.location.href).pathname;
          const active = pathname.endsWith('/main-dark.css') === isDarkMode;
          link.media = active ? 'all' : 'not all';
          link.disabled = !active;
        });
      }

      const toggle = $window.document.getElementById('dark-mode-toggle');
      if (toggle && toggle.mode !== theme) {
        toggle.mode = theme;
      }
    }

    function send(type, payload) {
      if (!instanceId || !parentOrigin) {
        return;
      }
      $window.parent.postMessage(
        {
          version: PROTOCOL_VERSION,
          type: type,
          instanceId: instanceId,
          payload: payload,
        },
        parentOrigin
      );
    }
  }

  function isEmbedded() {
    const params = new URLSearchParams(window.location.search);
    return params.get('embedded') === 'true' && window.parent !== window;
  }

  function getReferrerOrigin(referrer) {
    if (!referrer) {
      return null;
    }
    try {
      const origin = new URL(referrer).origin;
      return origin === 'null' ? null : origin;
    } catch (error) {
      return null;
    }
  }

  function isBridgeMessage(value) {
    return (
      value &&
      typeof value === 'object' &&
      value.version === PROTOCOL_VERSION &&
      typeof value.type === 'string' &&
      typeof value.instanceId === 'string' &&
      value.instanceId.length > 0 &&
      Object.prototype.hasOwnProperty.call(value, 'payload')
    );
  }

  function isBootstrapPayload(value, expectedRole) {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const auth = value.auth;
    const navigation = value.navigation;
    return (
      auth &&
      typeof auth.token === 'string' &&
      auth.token.length > 0 &&
      typeof auth.userId === 'string' &&
      auth.userId.length > 0 &&
      auth.userType === expectedRole &&
      (value.language === 'en' || value.language === 'es') &&
      (value.theme === 'light' || value.theme === 'dark') &&
      navigation &&
      typeof navigation.appKey === 'string' &&
      typeof navigation.customerId === 'string' &&
      typeof navigation.legacyState === 'string' &&
      (navigation.scope === 'customer' ||
        (navigation.scope === 'project' && typeof navigation.projectId === 'string'))
    );
  }

  function isAllowedState(state, role) {
    const prefixes = {
      Manager: ['member.project.', 'member.dataSource.'],
      Admin: ['member.customer.show.project.', 'member.customer.show.dataSource.'],
      SupportAdmin: [
        'member.var.show.customer.show.project.',
        'member.var.show.customer.show.dataSource.',
      ],
      SuperAdmin: [
        'member.var.show.customer.show.project.',
        'member.var.show.customer.show.dataSource.',
      ],
    };
    return (prefixes[role] || []).some(function (prefix) {
      return state.indexOf(prefix) === 0;
    });
  }

  function sanitizeStateParams(value) {
    const params = {};
    if (!value || typeof value !== 'object') {
      return params;
    }
    Object.keys(value).forEach(function (key) {
      const current = value[key];
      if (
        ALLOWED_PARAMS.has(key) &&
        (typeof current === 'string' || current === null)
      ) {
        params[key] = current;
      }
    });
    return params;
  }
})();
